// frontend/src/services/adminApi.js
//
// Espace d'administration (2026-09-12). Toutes ces routes sont gardees cote
// serveur par middleware/requireAdmin.js : ce fichier ne protege rien, il ne
// fait qu'appeler. Un non-administrateur recoit un 404 — ce qui est voulu
// (on ne confirme pas l'existence de cet espace).

import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './compositionApi';

// CODE D ACCES PARTAGE (2026-09-19).
//
// Demande : pouvoir ouvrir l'espace d'administration depuis n'importe quel
// compte de test, sans inscrire chaque adresse dans ADMIN_EMAILS.
//
// Il est garde dans sessionStorage, PAS dans localStorage : il disparait a
// la fermeture de l'onglet. Un code de consultation qui survivrait des
// semaines sur une machine partagee n'aurait plus grand sens.
//
// Il voyage dans un en-tete, jamais dans une URL — une URL finit dans les
// journaux du serveur et dans l'historique du navigateur.
const CLE_CODE = 'bookipix.admin.code';

export const lireCodeAdmin = () => {
  try {
    return sessionStorage.getItem(CLE_CODE) || '';
  } catch (_error) {
    // Navigation privee ou stockage bloque : on continue sans code.
    return '';
  }
};

export const poserCodeAdmin = (code) => {
  try {
    if (code) sessionStorage.setItem(CLE_CODE, code);
    else sessionStorage.removeItem(CLE_CODE);
  } catch (_error) { /* sans stockage, le code vaut pour cette page */ }
};

const authHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Session invalide. Merci de vous reconnecter.');
  const code = lireCodeAdmin();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
    ...(code ? { 'x-admin-code': code } : {})
  };
};

const request = async (path) => {
  const response = await fetch(`${getApiBaseUrl()}/admin${path}`, { headers: await authHeaders() });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || "Accès refusé.");
    error.status = response.status;
    throw error;
  }
  return payload;
};

// Sert uniquement a decider si le lien "Administration" s'affiche. Ne jamais
// s'en servir comme d'une protection : c'est le serveur qui tranche, route
// par route.
// Renvoie desormais { isAdmin, codeAttendu } : « codeAttendu » dit qu un
// code partage est configure sur ce serveur, donc que l interface doit
// proposer un champ plutot que de repondre « Page introuvable » a
// quelqu un qui a le droit d entrer. Le code lui-meme ne circule jamais
// dans ce sens.
// « connecte » (2026-11-02) : sans session (ou avec une session anonyme, que le serveur refuse toujours), aucune demande ne
// partait et l'ecran repondait « Page introuvable » — meme a qui avait le code. On dit alors de se connecter d'abord.
export const checkIsAdmin = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token || session.user?.is_anonymous) return { isAdmin: false, codeAttendu: false, connecte: false };
    const { isAdmin, codeAttendu } = await request('/me');
    return { isAdmin: Boolean(isAdmin), codeAttendu: Boolean(codeAttendu), connecte: true };
  } catch (_error) {
    return { isAdmin: false, codeAttendu: false, connecte: true };
  }
};

// Les travaux longs : fabrications de PDF et envois a l imprimeur.
// Premieres routes de cet espace qui AGISSENT au lieu de lire — d'ou le
// POST, et la confirmation cote ecran.
export const listJobs = () => request('/jobs');

const action = async (chemin, corps) => {
  const response = await fetch(`${getApiBaseUrl()}/admin${chemin}`, {
    method: 'POST',
    headers: await authHeaders(),
    ...(corps ? { body: JSON.stringify(corps) } : {})
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || payload?.raison || 'Action refusée.');
    error.status = response.status;
    error.missing = payload?.missing;
    throw error;
  }
  return payload;
};

export const stopPdfJob = (jobId) => action(`/jobs/pdf/${encodeURIComponent(jobId)}/stop`);
export const stopGelatoJob = (orderId) => action(`/jobs/gelato/${encodeURIComponent(orderId)}/stop`);
export const retryGelatoJob = (orderId) => action(`/jobs/gelato/${encodeURIComponent(orderId)}/retry`);
export const cleanupJobs = () => action('/jobs/cleanup');

export const listAllBooks = (search = '') => request(
  `/books${search ? `?search=${encodeURIComponent(search)}` : ''}`
);

// URL de consultation d'un livre. Le jeton ne peut pas voyager dans un
// en-tete via un <iframe> : on recupere donc le HTML nous-memes (avec
// l'en-tete d'authentification) et on l'injecte en srcDoc — jamais de jeton
// dans une URL, ou il finirait dans les journaux du serveur et l'historique
// du navigateur.
// Le collectif d'un livre en chiffres (refonte du collectif, phase 5).
export const getAdminBookCollective = (bookId) => request(`/books/${encodeURIComponent(bookId)}/collective`);

export const fetchBookPreviewHtml = async (bookId) => {
  const response = await fetch(`${getApiBaseUrl()}/admin/books/${bookId}/preview.html`, {
    headers: await authHeaders()
  });
  if (!response.ok) throw new Error("Impossible d'afficher ce livre.");
  return response.text();
};

// Journal des evenements metier (voir backend/services/events/eventLog.js).
//
// Repond a « que s'est-il passe sur cette commande ? » sans ouvrir de
// terminal, et identiquement quel que soit le serveur qui a agi — chaque
// evenement porte son environnement d'origine.
export const listEvents = ({ orderId, bookId, level, limit } = {}) => {
  const params = new URLSearchParams();
  if (orderId) params.set('orderId', orderId);
  if (bookId) params.set('bookId', bookId);
  if (level) params.set('level', level);
  if (limit) params.set('limit', String(limit));
  const suffixe = params.toString();
  return request(`/events${suffixe ? `?${suffixe}` : ''}`);
};

// Etat de sante du serveur : memoire, processeur, disque, rendus en cours,
// derniere sauvegarde. Ce qui se lit depuis Node, donc disponible sur les
// trois environnements.
export const fetchServerHealth = () => request('/health');

// Mode global test/production (2026-10-04, voir backend/services/settings/
// appMode.js) : un seul etat pour Stripe (vrais paiements) ET Gelato
// (vraies commandes facturees/imprimees). `missingForProduction` liste ce
// qu'il reste a configurer avant de pouvoir basculer — jamais devine cote
// ecran.
export const fetchAppMode = () => request('/mode');
// Apres un changement de mode, la pastille permanente de l'en-tete (shell/useAdminStatus.js) se met a jour tout de suite.
export const EVENEMENT_MODE_CHANGE = 'bookipix:admin-mode-changed';
export const setAppModeRemote = async (mode) => {
  const resultat = await action('/mode', { mode });
  try { window.dispatchEvent(new Event(EVENEMENT_MODE_CHANGE)); } catch (_erreur) { /* sans fenetre (tests) : tant pis */ }
  return resultat;
};

// Ce qui demande une action (pastilles rouges du menu) : commandes en echec, incident serveur recent.
export const fetchAdminAlerts = (visitsSince) => request(`/alerts${visitsSince ? `?visitsSince=${encodeURIComponent(visitsSince)}` : ''}`);

// Vider le journal des evenements (2026-10-03) : purement informatif, voir
// backend/services/events/eventLog.js.
export const purgeEventsRemote = () => action('/events/purge');

// Tarifs des livres (2026-10-07) — voir backend/services/pricing/pricingSettings.js.
export const getPricingSettingsRemote = () => request('/pricing');
export const setPricingSettingsRemote = async (reglages) => {
  const response = await fetch(`${getApiBaseUrl()}/admin/pricing`, {
    method: 'PUT',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(reglages)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || 'Mise à jour des tarifs refusée.');
    error.status = response.status;
    throw error;
  }
  return payload;
};

// Livres non finalises et sans aucune commande — a previsualiser avant de
// supprimer (voir backend/routes/admin.js, listUnfinalizedCandidates).
export const listUnfinalizedBooks = () => request('/books/unfinalized');
export const purgeUnfinalizedBooks = () => action('/books/unfinalized/purge');

// Comptes anonymes abandonnes (2026-10-04) — voir
// backend/services/accounts/anonymousPurge.js. `jours` par defaut : 7.
export const listAbandonedAnonymousAccounts = (jours) => request(`/accounts/anonymous/abandoned${jours ? `?jours=${jours}` : ''}`);
export const purgeAbandonedAnonymousAccounts = (jours) => action('/accounts/anonymous/abandoned/purge', jours ? { jours } : undefined);

// Fournisseurs d'impression et produits (Prodigi, etape 2) — voir
// backend/routes/adminPrintCatalog.js. Les montants voyagent en CENTIMES D'EURO.
const envoyer = async (methode, chemin, corps) => {
  const response = await fetch(`${getApiBaseUrl()}/admin${chemin}`, {
    method: methode,
    headers: await authHeaders(),
    ...(corps ? { body: JSON.stringify(corps) } : {})
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || 'Action refusée.');
    error.status = response.status;
    error.code = payload?.code;
    throw error;
  }
  return payload;
};

export const listPrintProviders = () => request('/print/providers');
export const setPrintProviderEnabled = (providerId, enabled) =>
  envoyer('PUT', `/print/providers/${encodeURIComponent(providerId)}`, { enabled });
export const listPrintProducts = () => request('/print/products');
export const savePrintProduct = (formatId, fiche) =>
  envoyer('PUT', `/print/products/${encodeURIComponent(formatId)}`, fiche);
export const deletePrintProduct = (formatId) =>
  envoyer('DELETE', `/print/products/${encodeURIComponent(formatId)}`);
export const quotePrintProduct = (formatId, { country, pageCount }) =>
  envoyer('POST', `/print/products/${encodeURIComponent(formatId)}/quote`, { country, pageCount });

// Refonte de l'Admin, phase 2 : test de connexion d'un fournisseur (lecture sans effet), photos d'un produit, historique.
export const pingPrintProvider = (providerId) => envoyer('POST', `/print/providers/${encodeURIComponent(providerId)}/ping`);
export const deleteProductPhoto = (formatId, slot) =>
  envoyer('DELETE', `/print/products/${encodeURIComponent(formatId)}/photos/${encodeURIComponent(slot)}`);
export const uploadProductPhoto = async (formatId, slot, fichier) => {
  const entetes = await authHeaders();
  delete entetes['Content-Type']; // le navigateur pose lui-meme le type multipart et sa frontiere
  const corps = new FormData();
  corps.append('photo', fichier);
  const response = await fetch(`${getApiBaseUrl()}/admin/print/products/${encodeURIComponent(formatId)}/photos/${encodeURIComponent(slot)}`, {
    method: 'PUT', headers: entetes, body: corps
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || 'Envoi de la photo refusé.');
    error.status = response.status;
    throw error;
  }
  return payload;
};
export const listAdminChanges = ({ area, key, limit } = {}) => {
  const params = new URLSearchParams();
  if (area) params.set('area', area);
  if (key) params.set('key', key);
  if (limit) params.set('limit', String(limit));
  const suffixe = params.toString();
  return request(`/changes${suffixe ? `?${suffixe}` : ''}`);
};


// Liste des testeurs (2026-10-17) — voir backend/routes/adminTesters.js.
export const listTesters = () => request('/testers');
export const addTester = (email, note) => envoyer('POST', '/testers', { email, note });
export const removeTester = (email) => envoyer('DELETE', `/testers/${encodeURIComponent(email)}`);

// Créer / supprimer un compte (2026-11-02) — voir backend/services/admin/adminAccounts.js (refus et ordre de suppression).
export const createAdminUser = (email, name) => envoyer('POST', '/users', { email, name });
export const getUserDeletion = (id) => request(`/users/${encodeURIComponent(id)}/deletion`);
export const deleteAdminUser = (id) => envoyer('DELETE', `/users/${encodeURIComponent(id)}`);

// Codes promo globaux (phase 2b) — voir backend/routes/adminPromoCodes.js.
export const listPromoCodes = () => request('/promo-codes');
export const createPromoCode = (code) => envoyer('POST', '/promo-codes', code);
export const updatePromoCode = (code, champs) => envoyer('PUT', `/promo-codes/${encodeURIComponent(code)}`, champs);
export const deletePromoCode = (code) => envoyer('DELETE', `/promo-codes/${encodeURIComponent(code)}`);

// Commandes (phase 3) — voir backend/routes/adminOrders.js.
export const listAdminOrders = ({ statut, fournisseur, mode, q, page } = {}) => {
  const params = new URLSearchParams();
  if (statut) params.set('statut', statut);
  if (fournisseur) params.set('fournisseur', fournisseur);
  if (mode) params.set('mode', mode);
  if (q) params.set('q', q);
  if (page && Number(page) > 1) params.set('page', String(page));
  const suffixe = params.toString();
  return request(`/orders${suffixe ? `?${suffixe}` : ''}`);
};
export const getAdminOrder = (id) => request(`/orders/${encodeURIComponent(id)}`);
export const retryAdminOrder = (id) => envoyer('POST', `/orders/${encodeURIComponent(id)}/retry`);
export const emailAdminOrder = (id, kind) => envoyer('POST', `/orders/${encodeURIComponent(id)}/email`, { kind });
// Suppression : uniquement des commandes de TEST (le serveur refuse les autres).
export const deleteAdminOrder = (id) => envoyer('DELETE', `/orders/${encodeURIComponent(id)}`);
export const purgeAdminTestOrders = () => envoyer('DELETE', '/orders/tests');

// Livres et utilisateurs (phase 4) — voir backend/routes/adminLibrary.js.
const avecParametres = (chemin, valeurs) => {
  const params = new URLSearchParams();
  Object.entries(valeurs).forEach(([cle, valeur]) => {
    if (cle === 'page' ? Number(valeur) > 1 : valeur) params.set(cle, String(valeur));
  });
  const suffixe = params.toString();
  return request(`${chemin}${suffixe ? `?${suffixe}` : ''}`);
};
export const listAdminLibrary = ({ etat, q, page } = {}) => avecParametres('/library', { etat, q, page });
export const listAdminUsers = ({ type, q, page } = {}) => avecParametres('/users', { type, q, page });

// Tableau de bord et trafic (phase 5b) — voir backend/routes/adminStats.js.
// Qui est en ligne en ce moment (2 dernieres minutes).
export const getAdminOnline = () => request('/online');

export const getAdminDashboard = ({ jours } = {}) => request(`/stats/dashboard${jours ? `?jours=${jours}` : ''}`);
export const getAdminTraffic = ({ jours } = {}) => request(`/stats/traffic${jours ? `?jours=${jours}` : ''}`);
// Détail d'une journée (heure de Paris) : chaque visite, ses pages et le temps passé. Sans jour : aujourd'hui.
export const getAdminTrafficDetail = ({ jour } = {}) => request(`/stats/traffic/detail${jour ? `?jour=${encodeURIComponent(jour)}` : ''}`);

// Journal des incidents d'exploitation (phase 6) — voir backend/services/admin/incidents.js.
export const listAdminIncidents = () => request('/incidents');
export const dismissAdminIncident = (id) => envoyer('DELETE', `/incidents/${encodeURIComponent(id)}`);
export const dismissAllAdminIncidents = () => envoyer('DELETE', '/incidents');
export const dismissServerIncident = () => envoyer('DELETE', '/incidents-serveur');

// Journal des e-mails (2026-10-23) — voir backend/services/email/emailJournal.js.
export const listAdminEmails = ({ jour, statut, q } = {}) => avecParametres('/emails', { jour, statut, q });
